import React, { useEffect, useReducer } from 'react';
import ReactDOM from 'react-dom';
import { v4 as uuidv4 } from 'uuid';

import { useHTTP } from 'hooks/useRequest';
import useErrorPush from 'utils/useErrorPush';

const PayPalButton = window.paypal
  ? window.paypal.Buttons.driver('react', {
      React,
      ReactDOM,
    })
  : null;

const initialState = {
  paypalData: {},
  loading: false,
  showPaypalError: false,
  paypalErrorMessage: '',
  hasApproval: false,
  approvalPostData: {},
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_PAYPAL_DATA':
      return { ...state, paypalData: action.payload };

    case 'SET_LOADING':
      return { ...state, loading: action.payload };

    case 'PAYPAL_APPROVE': {
      const {
        data,
        details,
        hasApproval,
        showPaypalError,
        paypalErrorMessage,
      } = action.payload;

      return {
        ...state,
        hasApproval,
        showPaypalError,
        paypalErrorMessage,
        approvalPostData: {
          orderID: data.orderID,
          payerID: data.payerID,
          token: details.id,
          status: details.status,
          transaction: details.purchase_units[0],
        },
      };
    }

    case 'SET_PAYPAL_ERROR_MESSAGE': {
      const { loading, showPaypalError, paypalErrorMessage } = action.payload;

      return { ...state, loading, showPaypalError, paypalErrorMessage };
    }

    default:
      return state;
  }
};

const PaymentPayPalButton = ({
  configGenericError,
  toggleGenericError,
  setPaymentStatus,
  show_payment_display_error: showPaymentDisplayError,
  payment_display_errors: paymentDisplayErrors,
  assets,
  paymentTotal,
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const requestConfig = {
    url: '/transactions/paypal/new',
    method: 'get',
  };
  const { data: paypalOrderData, error: paypalOrderDataError } =
    useHTTP(requestConfig);

  useEffect(() => {
    if (paypalOrderData) {
      dispatch({
        type: 'SET_PAYPAL_DATA',
        payload: paypalOrderData.data,
      });
    }
  }, [paypalOrderData]);

  useEffect(() => {
    if (typeof paypalOrderDataError === 'undefined') return;
    configGenericError({
      title: 'Error',
      body: (
        <div>
          <p>Unfortunately something has gone wrong.</p>
          <p>
            We are sorry for any inconvenience this may cause. Please try again
            later. If the problem persists, please contact our customer service
            team.
          </p>
        </div>
      ),
      errorButton: true,
      errorButtonTitle: 'Refresh Checkout',
      errorButtonAction: toggleGenericError,
      closeAction: toggleGenericError,
      reloadCheckoutAfterClose: true,
      reloadCheckoutAfterErrorButtonAction: true,
    });
    useErrorPush(
      'Checkout: Unable to retrieve paypal order information.',
      window.dataLayer,
    );
  }, [paypalOrderDataError]);

  const paypalApprovalRequestConfig = {
    method: 'POST',
    url: state.hasApproval ? '/transactions/paypal' : null,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    data: state.approvalPostData,
  };

  const {
    data: paypalOrderApproveResponseData,
    error: paypalOrderApproveResponseDataError,
  } = useHTTP(paypalApprovalRequestConfig || null);

  useEffect(() => {
    if (paypalOrderApproveResponseData) {
      window.location.href = decodeURIComponent(
        paypalOrderApproveResponseData.redirect_url,
      );
    }
  }, [paypalOrderApproveResponseData]);

  useEffect(() => {
    if (typeof paypalOrderApproveResponseDataError === 'undefined') return;
    configGenericError({
      title: 'Error',
      body: (
        <div>
          <p>Unfortunately something has gone wrong.</p>
          <p>
            We are sorry for any inconvenience this may cause. Please try again
            later. If the problem persists, please contact our customer service
            team.
          </p>
        </div>
      ),
      errorButton: true,
      errorButtonTitle: 'Refresh Checkout',
      errorButtonAction: toggleGenericError,
      closeAction: toggleGenericError,
      reloadCheckoutAfterClose: true,
      reloadCheckoutAfterErrorButtonAction: true,
    });
    useErrorPush(
      'Checkout: Sorry there has been a problem with PayPal, your payment has not been taken.',
      window.dataLayer,
    );
  }, [paypalOrderApproveResponseDataError]);

  const processPayPalPayment = () => {
    setPaymentStatus(true);
    dispatch({ type: 'SET_LOADING', payload: true });
  };

  const createOrder = (data, actions) => {
    processPayPalPayment();
    const {
      paypalData: {
        purchase_units: purchaseUnits,
        application_context: applicationContext,
      },
    } = state;
    return actions.order.create({
      purchase_units: purchaseUnits,
      application_context: applicationContext,
    });
  };

  const onApprove = (data, actions) => {
    dispatch({
      type: 'SET_LOADING',
      payload: true,
    });
    return actions.order.capture().then(details => {
      dispatch({
        type: 'PAYPAL_APPROVE',
        payload: {
          hasApproval: true,
          data,
          details,
          showPaypalError: false,
          paypalErrorMessage: '',
        },
      });
    });
  };

  const onError = () => {
    dispatch({
      type: 'SET_PAYPAL_ERROR_MESSAGE',
      payload: {
        loading: false,
        showPaypalError: true,
        paypalErrorMessage:
          'Sorry there has been a problem with PayPal, your payment has not been taken.',
      },
    });
    useErrorPush(
      'Checkout: Sorry there has been a problem with PayPal, your payment has not been taken.',
      window.dataLayer,
    );
  };

  const onCancel = () => {
    dispatch({
      type: 'SET_PAYPAL_ERROR_MESSAGE',
      payload: {
        loading: false,
        showPaypalError: true,
        paypalErrorMessage:
          'You have canceled the PayPal payment. Please choose a different payment method.',
      },
    });
    setPaymentStatus(false);
  };

  const Loading = () =>
    state.loading ? <div className="loader paypal">Loading....</div> : null;

  const ShowPaymentDisplayErrorSection = () => {
    if (showPaymentDisplayError) {
      return (
        <div className="showPaymentDisplayError">
          {paymentDisplayErrors.map(errorMessage => (
            <p key={uuidv4()}>{errorMessage}</p>
          ))}
        </div>
      );
    }

    if (state.showPaypalError) {
      return (
        <div className="showPaymentDisplayError">
          <p>{state.paypalErrorMessage}</p>
        </div>
      );
    }

    return false;
  };

  return (
    <div className="checkout-sub-panel payment">
      <ShowPaymentDisplayErrorSection />
      <Loading />
      <div className="paypal-payment-summary">
        <div className="paypal-warning">
          <p>
            <strong>You will be redirected to PayPal.</strong>
          </p>
          <div className="paypal-nav-away">
            <img
              src={assets.misc.paypalWarning}
              className="error-icon"
              alt="paypal-warning"
            />
            <div>
              <p className="main-warning">
                Please <strong>do not close the window or navigate away</strong>{' '}
                from the page.
              </p>
              <p>This may cause multiple payments to be taken.</p>
            </div>
          </div>
        </div>
        <p>
          Your PayPal account will be charged:{' '}
          <strong>&pound;{paymentTotal}</strong> for your delivery items and
          delivery charge (if applicable).
        </p>
        <div className="paypal-pay-button">
          <PayPalButton
            style={{ layout: 'horizontal', color: 'blue', tagline: false }}
            createOrder={(data, actions) => createOrder(data, actions)}
            onApprove={(data, actions) => onApprove(data, actions)}
            onError={(error, actions) => onError(error, actions)}
            onCancel={(data, actions) => onCancel(data, actions)}
          />
        </div>
      </div>
    </div>
  );
};

export default PaymentPayPalButton;
